import { Brief } from '@/domain/brief/Brief';
import type { BrandStatus, BudgetRange, ProjectType, Timeline } from '@/domain/brief/BriefOptions';
import { Email } from '@/domain/brief/Email';
import { fail, type Result, succeed } from '@/domain/shared/Result';

export const BRIEF_STEPS = ['project', 'scope', 'contact'] as const;
export type BriefStep = (typeof BRIEF_STEPS)[number];

export interface BriefDraftFields {
  readonly projectType: ProjectType | null;
  readonly brandStatus: BrandStatus | null;
  readonly budget: BudgetRange | null;
  readonly timeline: Timeline | null;
  readonly fullName: string;
  readonly email: string;
  readonly company: string;
  readonly phone: string;
  readonly message: string;
}

export type BriefField = keyof BriefDraftFields;
export type BriefErrorCode = 'required' | 'email-invalid' | 'too-long';
export type BriefErrors = Readonly<Partial<Record<BriefField, BriefErrorCode>>>;

const STEP_FIELDS: Readonly<Record<BriefStep, readonly BriefField[]>> = {
  project: ['projectType', 'brandStatus'],
  scope: ['budget', 'timeline'],
  contact: ['fullName', 'email', 'company', 'phone', 'message'],
};

const EMPTY_FIELDS: BriefDraftFields = {
  projectType: null,
  brandStatus: null,
  budget: null,
  timeline: null,
  fullName: '',
  email: '',
  company: '',
  phone: '',
  message: '',
};

/**
 * Brouillon immuable du brief, rempli étape par étape.
 * Chaque modification renvoie une nouvelle instance : idéal pour l'état React.
 */
export class BriefDraft {
  static readonly MESSAGE_MAX_LENGTH = 600;
  static readonly NAME_MAX_LENGTH = 120;

  private readonly fields: BriefDraftFields;

  private constructor(fields: BriefDraftFields) {
    this.fields = fields;
  }

  static empty(): BriefDraft {
    return new BriefDraft(EMPTY_FIELDS);
  }

  get values(): BriefDraftFields {
    return this.fields;
  }

  with<TField extends BriefField>(field: TField, value: BriefDraftFields[TField]): BriefDraft {
    if (this.fields[field] === value) return this;
    return new BriefDraft({ ...this.fields, [field]: value });
  }

  /** Valide uniquement les champs de l'étape demandée. */
  validate(step: BriefStep): BriefErrors {
    const errors: Partial<Record<BriefField, BriefErrorCode>> = {};
    for (const field of STEP_FIELDS[step]) {
      const error = this.validateField(field);
      if (error) errors[field] = error;
    }
    return errors;
  }

  isStepValid(step: BriefStep): boolean {
    return Object.keys(this.validate(step)).length === 0;
  }

  /** Valide l'ensemble du brouillon et produit l'entité `Brief`. */
  toBrief(): Result<Brief, BriefErrors> {
    const errors = BRIEF_STEPS.reduce<BriefErrors>(
      (all, step) => ({ ...all, ...this.validate(step) }),
      {},
    );
    const { projectType, budget, timeline } = this.fields;
    const email = Email.parse(this.fields.email);

    if (Object.keys(errors).length > 0 || !projectType || !budget || !timeline || !email.ok) {
      return fail(errors);
    }

    return succeed(
      new Brief({
        projectType,
        brandStatus: this.fields.brandStatus,
        budget,
        timeline,
        fullName: this.fields.fullName.trim(),
        email: email.value,
        company: optional(this.fields.company),
        phone: optional(this.fields.phone),
        message: optional(this.fields.message),
      }),
    );
  }

  private validateField(field: BriefField): BriefErrorCode | null {
    const { fields } = this;
    switch (field) {
      case 'projectType':
      case 'budget':
      case 'timeline':
        return fields[field] === null ? 'required' : null;
      case 'fullName':
        if (fields.fullName.trim() === '') return 'required';
        return fields.fullName.length > BriefDraft.NAME_MAX_LENGTH ? 'too-long' : null;
      case 'email': {
        const email = Email.parse(fields.email);
        return email.ok ? null : email.error;
      }
      case 'message':
        return fields.message.length > BriefDraft.MESSAGE_MAX_LENGTH ? 'too-long' : null;
      case 'brandStatus':
      case 'company':
      case 'phone':
        return null;
    }
  }
}

const optional = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};
